/**
 * @file QuickSight Dashboard Embed Component (SAML Compatible)
 * @description This component renders a QuickSight dashboard using the Amazon QuickSight Embedding SDK.
 * It retrieves the dashboard embed URL from an API and embeds the dashboard in a container element.
 * Updated to support both traditional Amplify Auth and SAML authentication.
 * Enhanced with comprehensive logging for debugging dashboard loading issues.
 * @requires react
 * @requires aws-amplify
 * @requires @material-ui/core/CircularProgress
 * @requires @material-ui/core/styles
 * @requires amazon-quicksight-embedding-sdk
 * @author SONAL JAIN
 * @created 11/05/2023
 * @updated for SAML support and enhanced logging
 */

import React from "react";
import { API, Auth } from "aws-amplify";
import CircularProgress from "@material-ui/core/CircularProgress";
import { withStyles } from "@material-ui/core/styles";
import { getSecret } from "../../Pages/config/ParameterStore";
import AccessKeys from "./AccessKeyAPI.Instance";
import { getSecretManager } from "../../Pages/config/SecretManager";
import { LogFileUploadToS3 } from "./LogFileUploadToS3";
import AWS from "aws-sdk";
import aws4 from "aws4";
// ✅ SAML Fix: Import token exchange lock handler
import { getSessionWithLock, clearSAMLCache } from '../../utils/samlCallbackHandler';
var QuickSightEmbedding = require("amazon-quicksight-embedding-sdk");

// Enhanced logging utility for QuickSight dashboard operations
const logQuickSightEvent = (message, data = {}) => {
  const timestamp = new Date().toISOString();

  // Create a properly formatted log entry with safe JSON serialization
  let dataString = "";
  if (Object.keys(data).length > 0) {
    try {
      dataString = ` | Data: ${JSON.stringify(
        data,
        (key, value) => {
          // Handle circular references and functions
          if (typeof value === "function") return "[Function]";
          if (typeof value === "object" && value !== null) {
            if (
              value.constructor &&
              value.constructor.name !== "Object" &&
              value.constructor.name !== "Array"
            ) {
              return `[${value.constructor.name}]`;
            }
          }
          return value;
        },
        2
      )}`;
    } catch (error) {
      dataString = ` | Data: [Object serialization failed: ${error.message}]`;
    }
  }

  const formattedLogEntry = `[${timestamp}] [QUICKSIGHT] ${message}${dataString}`;

  // Log the properly formatted entry to console
  console.log(formattedLogEntry);

  // Store log in a global array for potential S3 upload
  if (window.quickSightLogs) {
    window.quickSightLogs.push(formattedLogEntry);
  } else {
    window.quickSightLogs = [formattedLogEntry];
  }
};

// Helper function to upload QuickSight logs to S3
const uploadQuickSightLogsToS3 = async (logSuffix = "QUICKSIGHT_Log") => {
  try {
    logQuickSightEvent("Preparing to upload QuickSight logs to S3", {
      logSuffix,
    });

    // Check if we have a valid token first
    const token =
      localStorage.getItem("Token") || localStorage.getItem("accessToken");
    if (!token) {
      logQuickSightEvent(
        "No token available for /keys API call, skipping S3 upload"
      );
      return;
    }

    logQuickSightEvent("Token found, calling /keys API for S3 upload", {
      hasToken: !!token,
      tokenLength: token.length,
    });

    const res = await AccessKeys.get("/keys");

    if (res && res.data && res.data.access_key && res.data.secret_key) {
      logQuickSightEvent(
        "Successfully fetched access keys from API for S3 upload"
      );

      const secrets = await getSecretManager(
        res.data.access_key,
        res.data.secret_key,
        res.data.session_token,
        false
      );

      logQuickSightEvent(
        "Successfully fetched secrets from getSecretManager for S3 upload"
      );
      const BUCKET = process.env.REACT_APP_LOG_BUCKET;

      LogFileUploadToS3(
        window.quickSightLogs || [], // Upload QuickSight-specific logs
        secrets.s3AccessKey,
        secrets.s3SecretKey,
        BUCKET,
        logSuffix
      );

      logQuickSightEvent("QuickSight logs uploaded to S3 successfully", {
        bucket: BUCKET,
        suffix: logSuffix,
        logsCount: window.quickSightLogs ? window.quickSightLogs.length : 0,
      });
    } else {
      logQuickSightEvent(
        "AccessKeys API did not return expected keys for S3 upload",
        {
          response: res ? res.data : "no response",
          hasData: !!(res && res.data),
          hasAccessKey: !!(res && res.data && res.data.access_key),
          hasSecretKey: !!(res && res.data && res.data.secret_key),
        }
      );
    }
  } catch (error) {
    logQuickSightEvent("Error during QuickSight logs S3 upload process", {
      error: error.message,
      stack: error.stack,
    });
    console.error("QuickSight logs S3 upload error:", error);
  }
};

// Styles for the component
const useStyles = (theme) => ({
  loading: {
    alignContent: "center",
    justifyContent: "center",
    display: "flex",
    marginTop: theme.spacing(4),
  },
});

// Utility function to get AWS credentials from Cognito Identity Pool
async function getAWSCredentialsFromCognito(jwtToken) {
  logQuickSightEvent(
    "Starting AWS credentials retrieval from Cognito Identity Pool",
    {
      hasJwtToken: !!jwtToken,
      tokenLength: jwtToken ? jwtToken.length : 0,
    }
  );

  return new Promise((resolve, reject) => {
    AWS.config.region = process.env.REACT_APP_REGION;
    const identityPoolId = process.env.REACT_APP_IDENTITY_POOL_ID;
    const userPoolProvider =
      "cognito-idp." +
      process.env.REACT_APP_REGION +
      ".amazonaws.com/" +
      process.env.REACT_APP_USER_POOL_ID;

    logQuickSightEvent("Cognito Identity Pool configuration", {
      region: AWS.config.region,
      identityPoolId,
      userPoolProvider,
    });

    const cognitoIdentity = new AWS.CognitoIdentity();
    const params = {
      IdentityPoolId: identityPoolId,
      Logins: {
        [userPoolProvider]: jwtToken,
      },
    };

    cognitoIdentity.getId(params, function (err, data) {
      if (err) {
        logQuickSightEvent("Error getting Cognito Identity ID", {
          error: err.toString(),
        });
        return reject("getId error: " + err);
      }

      logQuickSightEvent("Successfully obtained Cognito Identity ID", {
        identityId: data.IdentityId,
      });

      cognitoIdentity.getCredentialsForIdentity(
        {
          IdentityId: data.IdentityId,
          Logins: params.Logins,
        },
        function (err, creds) {
          if (err) {
            logQuickSightEvent("Error getting credentials for identity", {
              error: err.toString(),
            });
            return reject("getCredentialsForIdentity error: " + err);
          }

          logQuickSightEvent("Successfully obtained AWS credentials", {
            hasAccessKeyId: !!creds.Credentials.AccessKeyId,
            hasSecretKey: !!creds.Credentials.SecretKey,
            hasSessionToken: !!creds.Credentials.SessionToken,
            expiration: creds.Credentials.Expiration,
          });

          resolve({
            accessKeyId: creds.Credentials.AccessKeyId,
            secretAccessKey: creds.Credentials.SecretKey,
            sessionToken: creds.Credentials.SessionToken,
          });
        }
      );
    });
  });
}

// Function to call QuickSight Embed API with signed request
export async function getQuickSightDashboardEmbedURL(authData, additionalParams = {}) {
  logQuickSightEvent("Starting QuickSight dashboard embed URL retrieval", {
    hasAuthData: !!authData,
    authDataKeys: authData ? Object.keys(authData) : [],
    additionalParams: Object.keys(additionalParams),
  });

  try {
    const jwtToken = authData.jwtToken;
    if (!jwtToken) {
      logQuickSightEvent("Missing JWT token in auth data", { authData });
      throw new Error("Missing JWT token");
    }

    logQuickSightEvent("JWT token validated", { tokenLength: jwtToken.length });

    // 1. Get AWS credentials from Cognito Identity Pool
    logQuickSightEvent(
      "Step 1: Getting AWS credentials from Cognito Identity Pool"
    );
    const credentials = await getAWSCredentialsFromCognito(jwtToken);

    if (!credentials.accessKeyId) {
      logQuickSightEvent("Failed to obtain AWS credentials", { credentials });
      throw new Error("Failed to obtain AWS credentials");
    }

    logQuickSightEvent("AWS credentials obtained successfully");

    // 2. Prepare and sign the API Gateway request
    const host = process.env.REACT_APP_QS_URL_HOST;
    const path = process.env.REACT_APP_QS_PATH;

    logQuickSightEvent("Step 2: Preparing API Gateway request", {
      host,
      path,
      envHost: process.env.REACT_APP_API_GATEWAY_HOST,
    });

    // Build query parameters
    const queryParams = new URLSearchParams({
      jwtToken: authData.jwtToken,
      payloadSub: authData.payloadSub,
      email: authData.email,
      authMethod: authData.authMethod,
      ...additionalParams, // Include additional parameters
    });

    const opts = {
      host,
      path: `${path}?${queryParams.toString()}`,
      service: "execute-api",
      region: process.env.REACT_APP_REGION,
      method: "GET", // or 'POST' if needed
      headers: {
        "Content-Type": "application/json",
      },
    };

    logQuickSightEvent("Signing API Gateway request with aws4");
    aws4.sign(opts, credentials);

    logQuickSightEvent("Request signed successfully", {
      hasAuthorizationHeader: !!opts.headers["Authorization"],
      hasAmzDateHeader: !!opts.headers["X-Amz-Date"],
      hasSecurityTokenHeader: !!opts.headers["X-Amz-Security-Token"],
    });

    // 3. Make the signed request (using fetch)
    logQuickSightEvent("Step 3: Making signed request to API Gateway");
    const requestUrl = `https://${host}${opts.path}`;

    const response = await fetch(requestUrl, {
      method: opts.method,
      headers: {
        ...opts.headers,
        "X-Amz-Date": opts.headers["X-Amz-Date"],
        Authorization: opts.headers["Authorization"],
        "X-Amz-Security-Token": opts.headers["X-Amz-Security-Token"],
      },
    });

    logQuickSightEvent("API Gateway response received", {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      url: requestUrl,
    });

    if (!response.ok) {
      const errorText = await response.text();
      logQuickSightEvent("API Gateway error response", {
        status: response.status,
        statusText: response.statusText,
        errorText,
      });
      throw new Error(
        "API Gateway error: " + response.status + " - " + errorText
      );
    }

    const responseData = await response.json();
    logQuickSightEvent("API Gateway response parsed successfully", {
      hasData: !!responseData,
      responseKeys: responseData ? Object.keys(responseData) : [],
    });

    return responseData;
  } catch (error) {
    logQuickSightEvent("Error in getQuickSightDashboardEmbedURL", {
      error: error.message,
      stack: error.stack,
    });
    console.error("Error in getQuickSightDashboardEmbedURL:", error);
    throw error;
  }
}

class Embed extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loader: true,
    };
    // Create a variable to store the mounted state of the component
    this.mounted = false;

    logQuickSightEvent("Embed component constructor called", {
      dashboardIdName: props.dashboardIdName,
      role: props.role,
      embedConsole: props.embedConsole,
      urlParam: props.urlParam,
    });
  }

  componentDidMount() {
    logQuickSightEvent("Embed component mounted", {
      dashboardIdName: this.props.dashboardIdName,
      timestamp: Date.now(),
    });
    console.log(
      "Embed componentDidMount",
      this.props.dashboardIdName,
      Date.now()
    );

    const { dashboardIdName } = this.props;

    logQuickSightEvent("Starting access keys retrieval from /keys endpoint");

    AccessKeys.get("/keys")
      .then((res) => {
        logQuickSightEvent("Access keys retrieved successfully", {
          hasData: !!res.data,
          hasAccessKey: !!(res.data && res.data.access_key),
          hasSecretKey: !!(res.data && res.data.secret_key),
          hasSessionToken: !!(res.data && res.data.session_token),
        });

        const secretNames = [
          "quick_sight_account_id",
          dashboardIdName,
          "quick_sight_cognito_role_arn",
          "quick_sight_identity_pool_id_uat",
          "quick_sight_data_login_uat",
          "quick_sight_region",
        ];

        logQuickSightEvent("Preparing to retrieve QuickSight secrets", {
          secretNames,
          dashboardIdName,
        });

        this.mounted = true;

        getSecretManager(
          res.data.access_key,
          res.data.secret_key,
          res.data.session_token,
          false
        )
          .then((secrets) => {
            logQuickSightEvent("Secret manager credentials obtained", {
              hasSecrets: !!secrets,
              secretKeys: secrets ? Object.keys(secrets) : [],
            });

            getSecret(secretNames)
              .then((secrets) => {
                logQuickSightEvent(
                  "QuickSight secrets retrieved successfully",
                  {
                    secretKeys: Object.keys(secrets),
                    hasAccountId: !!secrets["quick_sight_account_id"],
                    hasDashboardId: !!secrets[dashboardIdName],
                    hasRoleArn: !!secrets["quick_sight_cognito_role_arn"],
                    hasIdentityPoolId:
                      !!secrets["quick_sight_identity_pool_id_uat"],
                    hasDataLogin: !!secrets["quick_sight_data_login_uat"],
                    hasRegion: !!secrets["quick_sight_region"],
                  }
                );

                /* Access the secret values & call the function to retrieve the QuickSight dashboard embed URL 
                        when the component mounts */
                logQuickSightEvent(
                  "Initiating QuickSight dashboard embed URL retrieval",
                  {
                    accountId: secrets["quick_sight_account_id"],
                    dashboardId: secrets[dashboardIdName],
                    region: secrets["quick_sight_region"],
                  }
                );

                this.getQuickSightDashboardEmbedURL(
                  secrets["quick_sight_account_id"],
                  secrets[dashboardIdName],
                  secrets["quick_sight_cognito_role_arn"],
                  secrets["quick_sight_identity_pool_id_uat"],
                  secrets["quick_sight_data_login_uat"],
                  secrets["quick_sight_region"]
                );
              })
              .catch((error) => {
                logQuickSightEvent(
                  "Error retrieving secrets from Parameter Store",
                  {
                    error: error.message,
                    stack: error.stack,
                    secretNames,
                  }
                );
                console.error("Error retrieving secrets Parameter:", error);
              });
          })
          .catch((error) => {
            logQuickSightEvent("Error retrieving secrets from SecretManager", {
              error: error.message,
              stack: error.stack,
            });
            console.error(
              "Error retrieving secrets from SecretManager:",
              error
            );
          });
      })
      .catch((err) => {
        logQuickSightEvent("Error retrieving access keys from /keys endpoint", {
          error: err.message || err.toString(),
          stack: err.stack,
        });
        console.log("Error");
      });
  }
  componentWillUnmount() {
    logQuickSightEvent("Embed component will unmount", {
      dashboardIdName: this.props.dashboardIdName,
      timestamp: Date.now(),
      wasLoading: this.state.loader,
    });
    console.log(
      "Embed componentWillUnmount",
      this.props.dashboardIdName,
      Date.now()
    );

    // Upload final logs to S3 before component unmounts
    setTimeout(() => {
      logQuickSightEvent("Initiating S3 upload for component unmount");
      uploadQuickSightLogsToS3("QUICKSIGHT_UNMOUNT_Log");
    }, 500);

    // Set mounted to false when the component unmounts
    this.mounted = false;
  }

  // Helper function to get authentication data based on auth method
  getAuthData = async () => {
    const authMethod = localStorage.getItem("authMethod");

    logQuickSightEvent("Getting authentication data", {
      authMethod,
      hasAuthMethod: !!authMethod,
    });

    if (authMethod === "SAML") {
      logQuickSightEvent("Processing SAML authentication data");

      // For SAML authentication, get data from localStorage
      const idToken = localStorage.getItem("idToken");
      const userEmail = localStorage.getItem("userEmail");
      const userSub = localStorage.getItem("userSub");

      logQuickSightEvent("SAML localStorage data check", {
        hasIdToken: !!idToken,
        hasUserEmail: !!userEmail,
        hasUserSub: !!userSub,
        tokenLength: idToken ? idToken.length : 0,
      });

      if (!idToken || !userEmail || !userSub) {
        logQuickSightEvent("Missing SAML authentication data in localStorage", {
          hasIdToken: !!idToken,
          hasUserEmail: !!userEmail,
          hasUserSub: !!userSub,
        });
        throw new Error("SAML authentication data not found in localStorage");
      }

      // Validate token format
      const tokenParts = idToken.split(".");
      if (tokenParts.length !== 3) {
        logQuickSightEvent("Invalid JWT token format", {
          tokenParts: tokenParts.length,
          tokenStart: idToken.substring(0, 20),
        });
        throw new Error("Invalid JWT token format");
      }

      try {
        // Decode the JWT token to get payload
        const tokenPayload = JSON.parse(atob(tokenParts[1]));

        logQuickSightEvent("JWT token decoded successfully", {
          hasPayload: !!tokenPayload,
          exp: tokenPayload.exp,
          iss: tokenPayload.iss,
          sub: tokenPayload.sub,
        });

        // Check if token is expired
        const currentTime = Math.floor(Date.now() / 1000);
        if (tokenPayload.exp && tokenPayload.exp < currentTime) {
          logQuickSightEvent("JWT token has expired", {
            exp: tokenPayload.exp,
            currentTime,
            expiredBy: currentTime - tokenPayload.exp,
          });
          throw new Error("Token has expired");
        }

        // Validate issuer matches your Cognito User Pool
        const expectedIssuer =
          "https://cognito-idp." +
          process.env.REACT_APP_REGION +
          ".amazonaws.com/" +
          process.env.REACT_APP_USER_POOL_ID;
        if (tokenPayload.iss !== expectedIssuer) {
          logQuickSightEvent("JWT token issuer mismatch", {
            expected: expectedIssuer,
            actual: tokenPayload.iss,
          });
          throw new Error("Token issuer does not match expected User Pool");
        }

        const authData = {
          jwtToken: idToken,
          payloadSub: userSub,
          email: userEmail,
          authMethod: "SAML",
          tokenPayload: tokenPayload,
        };

        logQuickSightEvent("SAML authentication data prepared successfully", {
          hasJwtToken: !!authData.jwtToken,
          payloadSub: authData.payloadSub,
          email: authData.email,
          authMethod: authData.authMethod,
        });

        return authData;
      } catch (error) {
        logQuickSightEvent("Error validating SAML token", {
          error: error.message,
          stack: error.stack,
        });
        console.error("Error validating SAML token:", error);
        throw new Error("Invalid SAML token: " + error.message);
      }
    } else {
      logQuickSightEvent("Processing Amplify authentication data");

      // For traditional Amplify Auth
      try {
        const user = await Auth.currentAuthenticatedUser();
        logQuickSightEvent("Amplify current user obtained", {
          hasUser: !!user,
          username: user?.username,
        });
        // Session data logged through logQuickSightEvent above

        // ✅ SAML Fix: Use token exchange lock to prevent double token exchange
        const data = await getSessionWithLock();
        logQuickSightEvent("Session obtained (with SAML deduplication)", {
          hasSession: !!data,
          isValid: data?.isValid(),
        });

        // Validate session is still valid
        if (!data.isValid()) {
          logQuickSightEvent("Amplify session is no longer valid");
          throw new Error("Session is no longer valid");
        }

        const jwtToken = data.idToken.jwtToken;
        const payloadSub = data.idToken.payload.sub;
        const email = data.idToken.payload.email;

        const authData = {
          jwtToken,
          payloadSub,
          email,
          authMethod: "Amplify",
        };

        logQuickSightEvent(
          "Amplify authentication data prepared successfully",
          {
            hasJwtToken: !!authData.jwtToken,
            payloadSub: authData.payloadSub,
            email: authData.email,
            authMethod: authData.authMethod,
            tokenLength: jwtToken ? jwtToken.length : 0,
          }
        );

        return authData;
      } catch (error) {
        // ✅ SAML Fix: Clear cache on auth error
        clearSAMLCache();
        logQuickSightEvent("Error getting Amplify auth data", {
          error: error.message,
          stack: error.stack,
        });
        console.error("Error getting Amplify auth data:", error);
        throw error;
      }
    }
  };

  // Function to retrieve the QuickSight dashboard embed URL
  getQuickSightDashboardEmbedURL = async (
    accountId,
    dashboardId,
    roleArn,
    IdentityPoolId,
    logins,
    region
  ) => {
    console.log('roleArn', roleArn);
    logQuickSightEvent(
      "Starting QuickSight dashboard embed URL retrieval process",
      {
        accountId,
        dashboardId,
        roleArn,
        IdentityPoolId,
        logins,
        region,
        userRole: this.props.role,
        embedConsole: this.props.embedConsole,
        urlParam: this.props.urlParam,
      }
    );

    try {
      // Get authentication data based on the auth method
      logQuickSightEvent("Getting authentication data for QuickSight");
      const authData = await this.getAuthData();
      // Authentication data logged through logQuickSightEvent above and below

      logQuickSightEvent("Authentication data obtained successfully", {
        authMethod: authData.authMethod,
        hasJwtToken: !!authData.jwtToken,
        email: authData.email,
        payloadSub: authData.payloadSub,
      });

      // Import necessary functions from QuickSight Embedding SDK
      logQuickSightEvent("Creating QuickSight embedding context");
      const { createEmbeddingContext } = QuickSightEmbedding;

      const embeddingContext = await createEmbeddingContext({
        onChange: (changeEvent, metadata) => {
          logQuickSightEvent("QuickSight context change event", {
            eventName: changeEvent?.eventName,
            metadata,
          });
          // Event data logged through logQuickSightEvent above
        },
        onEmbeddableEvent: (event) => {
          logQuickSightEvent("QuickSight embeddable event", {
            eventName: event?.eventName,
            eventData: event,
          });
          // Event data logged through logQuickSightEvent above
        },
        onError: (error) => {
          logQuickSightEvent("QuickSight embedding error", {
            error: error.message || error.toString(),
            errorType: error.type,
            errorCode: error.code,
          });
          // Error data logged through logQuickSightEvent above
        },
        // timeout: 60000 // Set timeout to 60 seconds (adjust as needed)
      });

      logQuickSightEvent("QuickSight embedding context created successfully");

      // Get the container element for embedding the QuickSight dashboard
      const containerDiv = document.getElementById("dashboardContainer");
      logQuickSightEvent("Dashboard container element check", {
        hasContainer: !!containerDiv,
        containerId: "dashboardContainer",
      });

      // Call the API to retrieve the QuickSight dashboard embed URL
      const apiEndpoint = process.env.REACT_APP_AMPLIFY_API_ENDPOINT_NAME;
      const apiPath = process.env.REACT_APP_QS_RESOURCE_PATH;

      logQuickSightEvent("Preparing API call to get QuickSight embed URL", {
        apiEndpoint,
        apiPath,
        hasJwtToken: !!authData.jwtToken,
        authMethod: authData.authMethod,
      });

      // Call the exported function that uses the correct endpoint
      const responseData = await getQuickSightDashboardEmbedURL(authData, {
          headers: {},
        response: true,
          jwtToken: authData.jwtToken,
          payloadSub: authData.payloadSub,
          email: authData.email,
          accountId: accountId,
          dashboardId: dashboardId,
          roleArn: roleArn,
          IdentityPoolId: IdentityPoolId,
          logins: logins,
          region: region,
          userRole: this.props.role,
          embedConsole: this.props.embedConsole,
          urlParam: this.props.urlParam,
          authMethod: authData.authMethod,
        
      });

      logQuickSightEvent("QuickSight API response received from exported function", {
        hasData: !!responseData,
        hasEmbedUrl: !!(responseData && responseData.EmbedUrl),
      });

      const originalUrl = responseData.data.EmbedUrl;

      logQuickSightEvent("QuickSight embed URL extracted", {
        hasUrl: !!originalUrl,
        urlLength: originalUrl ? originalUrl.length : 0,
        urlDomain: originalUrl ? new URL(originalUrl).hostname : "unknown",
      });

      // Set options for embedding the QuickSight dashboard
      logQuickSightEvent("Preparing QuickSight embedding options", {
        hasUrl: !!originalUrl,
        hasContainer: !!containerDiv,
        embedConsole: this.props.embedConsole,
      });

      const options = {
        url: originalUrl,
        container: containerDiv,
        scrolling: "no",
        footerPaddingEnabled: false,
        height: "100%", // Set the height of the container div to 100% of its parent
        width: "100%", // Set the width of the container div to 100% of its parent
        onEmbeddingComplete: (params) => {
          const { width, height } = params;
          logQuickSightEvent("QuickSight embedding completed", {
            width,
            height,
            dashboardId: this.props.dashboardIdName,
          });
          containerDiv.style.height = height + "px"; // Adjust the container div's height to match the embedded dashboard's height
        },
        onChange: (changeEvent, metadata) => {
          logQuickSightEvent("QuickSight frame change event", {
            eventName: changeEvent.eventName,
            metadata,
          });
          switch (changeEvent.eventName) {
            case "FRAME_MOUNTED": {
              logQuickSightEvent("QuickSight frame mounted successfully");
              console.log("Do something when the experience frame is mounted.");
              break;
            }
            case "FRAME_LOADED": {
              logQuickSightEvent("QuickSight frame loaded successfully");
              console.log("Do something when the experience frame is loaded.");
              break;
            }
          }
        },
      };

      const contentOptions = {
        locale: "en-US",
      };

      logQuickSightEvent("Starting QuickSight embedding process", {
        isMounted: this.mounted,
        embedConsole: this.props.embedConsole,
        contentOptions,
      });

      if (this.mounted) {
        try {
          if (this.props.embedConsole) {
            logQuickSightEvent("Embedding QuickSight console");
            await embeddingContext.embedConsole(options, contentOptions);
          } else {
            logQuickSightEvent("Embedding QuickSight dashboard");
            await embeddingContext.embedDashboard(options, contentOptions);
          }

          logQuickSightEvent(
            "QuickSight embedding process completed successfully"
          );

          // Upload success logs to S3 after successful embedding
          setTimeout(() => {
            logQuickSightEvent(
              "Initiating S3 upload for successful QuickSight embedding"
            );
            uploadQuickSightLogsToS3("QUICKSIGHT_EMBEDDING_SUCCESS_Log");
          }, 1000); // Delay to ensure all embedding events are captured
        } catch (error) {
          logQuickSightEvent("Error during QuickSight embedding", {
            error: error.message || error.toString(),
            stack: error.stack,
            dashboardId: this.props.dashboardIdName,
          });
          console.error("Error embedding QuickSight dashboard:", error);

          // Upload error logs to S3 after embedding failure
          setTimeout(() => {
            logQuickSightEvent(
              "Initiating S3 upload for QuickSight embedding error"
            );
            uploadQuickSightLogsToS3("QUICKSIGHT_EMBEDDING_ERROR_Log");
          }, 1000);
          // Handle error as needed
        }
      } else {
        logQuickSightEvent("Component not mounted, skipping embedding", {
          mounted: this.mounted,
        });
      }

      // Hide the loader once the embedding is complete
      logQuickSightEvent("Hiding loader after embedding process");
      this.setState({ loader: false });
    } catch (error) {
      logQuickSightEvent(
        "Error in getQuickSightDashboardEmbedURL main catch block",
        {
          error: error.message || error.toString(),
          stack: error.stack,
          dashboardId: this.props.dashboardIdName,
        }
      );
      console.error("Error in getQuickSightDashboardEmbedURL:", error);
      this.setState({ loader: false });

      // Upload error logs to S3 after main process failure
      setTimeout(() => {
        logQuickSightEvent(
          "Initiating S3 upload for QuickSight main process error"
        );
        uploadQuickSightLogsToS3("QUICKSIGHT_MAIN_ERROR_Log");
      }, 1000);
      // You might want to show an error message to the user here
    }
  };

  render() {
    const { classes } = this.props;

    return (
      <div>
        {this.state.loader && (
          <div className={classes.loading}>
            {" "}
            <CircularProgress />{" "}
          </div>
        )}
        <div id="dashboardContainer" className="autoFit"></div>
      </div>
    );
  }
}
export default withStyles(useStyles)(Embed);
